import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { scrollbarStyles } from '../../../styleSheet';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  IconButton,
  Skeleton,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import BaseModal from '../../templates/BaseModal';
import DetailDataTable from '../../templates/DetailDataTable';
import ImageCarousel from '../../templates/ImageCarousel';
import DetailBlock from '../../templates/DetailBlock';
import CloseIcon from '@mui/icons-material/Close';
import {
  INACTIVE_PROMOTION_TASK,
  MISSING_PROMOTION_TASK,
  PRICE_MISMATCH_TASK,
  resolveLegacyTaskType,
} from 'core/slices/utils/constants';
import { resolveTaskPrices } from 'core/slices/utils/priceTasks';
import { isEmptyIdentifier } from 'core/slices/utils/taskIdentifiers';
import { shouldRenderCarousel } from 'core/slices/utils/taskDetailsFeatures';
import { DONE_STATUSES, getAisleDisplay, getOperationalIdentifier, getStatusColor, getStatusStyle, buildDetailRows } from '../taskUtils';
import TaskBarcodeButton from './TaskBarcodeButton';

// ── Styles ────────────────────────────────────────────────────────────────

const styles = {
  // Container
  mainContainer: { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },

  // Header
  header: { bgcolor: 'background.paper', px: 2.5, pt: 2.5, pb: 2, flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider' },
  headerContent: { display: 'flex', alignItems: 'flex-start', gap: 1.5 },
  headerIcon: {
    width: 44, height: 44, borderRadius: 2.5, bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.25,
  },
  headerIconSpan: { fontSize: 22, color: 'text.secondary' },
  headerText: { flexGrow: 1, minWidth: 0 },
  headerTitle: { lineHeight: 1.35 },
  headerMeta: { display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0, mt: 0.3 },
  headerBadge: { display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.75, px: 1.25, py: 0.4, borderRadius: 1.5, fontSize: '0.7rem' },
  headerBadgeIcon: { fontSize: 13 },
  closeButton: { ml: 0.5, flexShrink: 0 },

  // Scrollable body
  body: {
    flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 2,
    ...scrollbarStyles,
  },
  bodyContent: { display: 'flex', flexDirection: 'column', gap: 2 },
  heroImageWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  // Adjustment banner
  adjustmentBanner: {
    bgcolor: '#e91e63', px: 2.5, py: 0.9,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
    flexShrink: 0,
  },
  adjustmentBannerIcon: { fontSize: 16, color: '#fff' },
  adjustmentBannerText: { color: '#fff', fontSize: '0.78rem', letterSpacing: 0.3 },

  // Footer
  footer: {
    bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
    px: 2.5, flexShrink: 0, width: '100%', height: 100,
    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
  },

  // Adjustment mode
  adjustmentMode: { position: 'relative', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  adjustmentTextField: {
    width: { xs: 130, sm: 180 },
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      bgcolor: 'background.paper',
      '& fieldset': { border: '1.5px solid', borderColor: 'divider' },
      '&:hover fieldset': { borderColor: 'text.disabled' },
      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '1.5px' },
    },
    '& input': { fontSize: '0.95rem', color: 'text.primary', py: 1 },
  },
  adjustmentButtons: {
    position: 'absolute',
    left: { xs: 'calc(50% + 75px)', sm: 'calc(50% + 100px)' },
    display: 'flex', alignItems: 'center', gap: 1.5,
  },
  adjustmentCloseButton: {
    width: 40, height: 40, bgcolor: theme => theme.bruno.colors.inactive, color: 'text.secondary', flexShrink: 0,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': { bgcolor: 'action.hover', transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' },
  },
  adjustmentCheckButton: {
    width: 40, height: 40, flexShrink: 0,
    bgcolor: '#e91e63', color: '#fff',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    '&:hover': { bgcolor: '#c2185b', transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(233,30,99,0.45)' },
    '&:disabled': { bgcolor: '#e91e63', color: '#fff', opacity: 0.4 },
  },
  adjustmentCheckButtonDisabled: { boxShadow: 'none' },
  adjustmentButtonIcon: { fontSize: 20 },

  // Loading skeletons
  skeletonsContainer: { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, width: '100%' },
  skeletonItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 },

  // Pending state
  pendingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 },
  pendingIcon: {
    width: 44, height: 44, borderRadius: '50%',
    bgcolor: theme => theme.bruno.colors.inactive,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  pendingIconSpan: { fontSize: 20, color: 'text.disabled' },
  pendingText: { fontSize: '0.7rem', color: 'text.disabled', lineHeight: 1.2 },

  // Status buttons
  statusButtonsContainer: { display: 'flex', flexWrap: 'nowrap', justifyContent: 'center', gap: { xs: 1, sm: 2 }, width: '100%' },
  statusButton: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
    cursor: 'pointer', userSelect: 'none',
    minWidth: { xs: 44, sm: 52 },
    flex: '1 1 0',
    maxWidth: { xs: 64, sm: 80 },
    '&:hover .status-circle': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.22)',
    },
    '&:active .status-circle': { transform: 'scale(0.92)' },
  },
  statusButtonDisabled: {
    cursor: 'default',
    opacity: 0.5,
    '&:hover .status-circle': {
      transform: 'none',
      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.22)',
    },
    '&:active .status-circle': { transform: 'none' },
  },
  statusCircle: {
    width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 }, borderRadius: '50%',
    bgcolor: 'background.paper',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.22)',
  },
  statusIcon: { fontSize: 17 },
  statusLabel: {
    fontSize: { xs: '0.58rem', sm: '0.65rem' },
    color: 'text.secondary', lineHeight: 1.2, fontWeight: 500,
    overflow: 'hidden', display: '-webkit-box',
    WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
  },

};

// ── Main modal ────────────────────────────────────────────────────────────────

export default function TasksDetails({
  open,
  task,
  onClose,
  carouselImages,
  barcodeUrl,
  loadingImages,
  loadingTasksStatus,
  tasksStatus,
  tasksStatusOverride,
  progressTasksType,
  resourcesTasksType,
  placementInformation,
  categories,
  currentStore,
  currentTaskType,
  contextRef,
  resetPagination,
  getImages,
  updateTasks,
  getTaskList,
  getTasksProgress,
  getAislesByTasks,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [adjustmentMode, setAdjustmentMode] = useState(false);
  const [realStock, setRealStock] = useState('');
  const [loadingImgs, setLoadingImgs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [cachedTask, setCachedTask] = useState(task);
  const [locationsPage, setLocationsPage] = useState(1);

  useEffect(() => {
    if (task) setCachedTask(task);
  }, [task]);

  useEffect(() => {
    setLocationsPage(1);
  }, [task?.task_id, open]);

  // Track which task_id the current in-flight image request belongs to
  const pendingTaskIdRef = useRef(null);
  // Guard against a stale `loadingImages=false` (from a previous task that
  // resolved after the modal closed) turning off the skeleton before the new
  // request has transitioned the slice into `loading: true`.
  const loadingStartedRef = useRef(false);

  // contextRef is owned by TasksView and updated on every render there.
  // Using the parent's ref ensures contextChanged() sees the real live context
  // even if this modal is mid-unmount or hasn't re-rendered yet.
  const localContextRef = useRef({ store: currentStore, taskType: currentTaskType });
  localContextRef.current = { store: currentStore, taskType: currentTaskType };
  const activeContextRef = contextRef ?? localContextRef;

  useEffect(() => {
    if (!open || !task) return;
    pendingTaskIdRef.current = task.task_id;
    loadingStartedRef.current = false;
    setAdjustmentMode(false);
    setRealStock('');
    setLoadingImgs(true);
    setSelectedStatus(null);
    getImages(task.task_id, task.task_type);
    return () => {
      pendingTaskIdRef.current = null;
      loadingStartedRef.current = false;
    };
  }, [open, task, task?.task_id, task?.task_type, getImages]);

  useEffect(() => {
    if (pendingTaskIdRef.current !== task?.task_id) return;
    if (loadingImages) {
      loadingStartedRef.current = true;
      return;
    }
    if (loadingStartedRef.current && loadingImgs) setLoadingImgs(false);
  }, [loadingImages, loadingImgs, task?.task_id]);

  const displayTask = task || cachedTask;

  const handleUpdateStatus = useCallback(async (taskId, taskType, status, realStock, creationTimestamp) => {
    setSubmitting(true);
    setSelectedStatus(status);
    // Capture context at start vs. read it LIVE after await. Using the callback's
    // closure for the post-await read would always see the same values it was
    // created with — a context change while updateTasks is pending would be
    // invisible, and we'd refresh lists belonging to a view the user left.
    const keyAtStart = `${currentStore}|${currentTaskType}`;
    const contextChanged = () =>
      keyAtStart !== `${activeContextRef.current.store}|${activeContextRef.current.taskType}`;

    try {
      // Slice swallows errors and returns { success }. Do NOT rely on try/catch
      // here — the await will never reject.
      const result = await updateTasks(taskId, taskType, status, realStock, creationTimestamp);
      if (contextChanged()) return !!result?.success;
      // A stale task also refreshes the list and closes the modal: the row now
      // holds a newer task.
      if (!result?.success && !result?.staleTask) return false;

      // Refresh list only AFTER success. Skip clearTaskList to avoid a full-list
      // skeleton flash when the modal closes on top of a background list.
      // getTaskList(0) always replaces the paginated list with just page 0's
      // results, so TasksView's page/hasMore must be reset in the same tick —
      // otherwise a stale hasMore=false from an earlier full scroll survives
      // this reload and blocks loading the remaining pages.
      resetPagination?.();
      getTaskList(0);
      getTasksProgress();
      getAislesByTasks();
      return true;
    } finally {
      setSubmitting(false);
      setSelectedStatus(null);
    }
  }, [currentStore, currentTaskType, activeContextRef, updateTasks, getTaskList, getTasksProgress, getAislesByTasks, resetPagination]);

  const handleSubmitAdjustment = useCallback(async () => {
    const ok = await handleUpdateStatus(
      displayTask?.task_id, displayTask?.task_type, 'stock_unavailable', realStock, displayTask?.creation_timestamp,
    );
    if (ok) onClose();
  }, [handleUpdateStatus, displayTask, realStock, onClose]);

  const handleSubmitStatus = useCallback(async (status) => {
    const ok = await handleUpdateStatus(
      displayTask?.task_id, displayTask?.task_type, status, undefined, displayTask?.creation_timestamp,
    );
    if (ok) onClose();
  }, [handleUpdateStatus, displayTask, onClose]);

  // tasksStatusOverride comes from TasksView (fetched once per task type, like mobile)
  // tasksStatus from Redux is the fallback
  const activeTasksStatus = useMemo(() => tasksStatusOverride ?? tasksStatus ?? [], [tasksStatusOverride, tasksStatus]);

  // Memoize reversed status array (must be before early return)
  const reversedTasksStatus = useMemo(() => [...activeTasksStatus].reverse(), [activeTasksStatus]);

  // Allow adjusting to zero; only reject empty or non-numeric input.
  const isValidRealStock = realStock !== '' && !isNaN(Number(realStock)) && Number(realStock) >= 0;

  if (!displayTask) return null;

  const {
    description, item, ean, store_info, task_type, consecutive_alerts,
  } = displayTask;

  // `resourcesTasksType` and `progressTasksType` are keyed by the name the API
  // sent, so they need the raw `task_type`. Every check below is the app's own
  // decision, so it uses the canonical name.
  const canonicalTaskType = resolveLegacyTaskType(task_type);

  const productTitle = description || displayTask.product_description;
  const resourceInfo = resourcesTasksType?.find(r => r.task_type === task_type);
  const ask_real_stock = resourceInfo?.ask_real_stock ?? false;
  const taskTypeLabel = t(`bruno.tasks_names.${canonicalTaskType}`, canonicalTaskType);
  const aisleDisplay = getAisleDisplay(displayTask, t);

  const isPromotion = canonicalTaskType === INACTIVE_PROMOTION_TASK || canonicalTaskType === MISSING_PROMOTION_TASK;
  // TODO: UNIMPLEMENTED_TASK not handled here — mobile does not use it either
  const showConsecutiveAlertsInfo = consecutive_alerts !== undefined && consecutive_alerts !== null && consecutive_alerts > 0;
  const isActionable = progressTasksType?.[task_type]?.actionable !== false;
  const isCompletedTask = DONE_STATUSES.has(displayTask?.status);

  // Header subtitle
  const itemVal = isPromotion
    ? (item && item !== '0' ? item : (store_info?.associated_item && store_info?.associated_item !== '0' ? store_info?.associated_item : '-'))
    : item;
  const eanVal = isPromotion
    ? (ean && ean !== '0' ? ean : (store_info?.associated_ean && store_info?.associated_ean !== '0' ? store_info?.associated_ean : '-'))
    : ean;

  // Detail rows
  const detailRows = buildDetailRows(displayTask, { placementInformation, categories, t });
  const showCarousel = shouldRenderCarousel(carouselImages, barcodeUrl);
  // Same helper mobile uses, so both platforms print the same number under the
  // code: the item when there is one, the EAN otherwise.
  const barcodeIdentifier = getOperationalIdentifier({ item: itemVal, ean: eanVal }, t);
  // Every price read on the shelf label. Normalized in core so the Bigtable
  // "prices" list and the flat Cloud SQL pair render through one path while
  // stores migrate.
  const taskPrices = canonicalTaskType === PRICE_MISMATCH_TASK
    ? resolveTaskPrices(displayTask)
    : [];
  const expectedLocations = store_info?.master_positions ?? [];
  const locationsPageSize = isMobile ? 4 : 6;
  const totalLocationPages = Math.max(1, Math.ceil(expectedLocations.length / locationsPageSize));
  const paginatedLocations = expectedLocations.slice(
    (locationsPage - 1) * locationsPageSize,
    locationsPage * locationsPageSize
  );

  const content = (
    <Box sx={styles.mainContainer}>
        {/* ── HEADER ── */}
        <Box sx={styles.header}>
          <Box sx={styles.headerContent}>
            {/* Task type icon box */}
            <Box sx={styles.headerIcon}>
              <Box component="span" className="material-icons" sx={styles.headerIconSpan}>inventory_2</Box>
            </Box>

            {/* Title + meta + badge */}
            <Box sx={styles.headerText}>
              <Typography variant="subtitle1" fontWeight={700} noWrap sx={styles.headerTitle}>
                {productTitle}
              </Typography>
              <Box sx={styles.headerMeta}>
                <Typography variant="caption" color="text.secondary">
                  {t('bruno.tasks_details.item', 'Item')} <strong>{itemVal}</strong>
                </Typography>
                {!isEmptyIdentifier(eanVal) && (
                  <Typography variant="caption" color="text.secondary">
                    {t('bruno.tasks_details.barcode', 'CB')} <strong>{eanVal}</strong>
                  </Typography>
                )}
                {/* Sits in the meta row next to Item. The row already wraps, so
                    the control needs no layout change of its own. */}
                <TaskBarcodeButton
                  barcodeUrl={barcodeUrl}
                  identifier={barcodeIdentifier}
                  productName={productTitle}
                />
                <Typography variant="caption" color="text.secondary">
                  {aisleDisplay.title} <strong>{aisleDisplay.value}</strong>
                </Typography>
                {/* The overhead landmark renders in the detail block below,
                    together with its age and freshness state. */}
                <Typography variant="caption" color="text.secondary">
                  {t('bruno.tasks.type', 'Type')} <strong>{taskTypeLabel}</strong>
                </Typography>
              </Box>
              {/* Alerts badge */}
              { showConsecutiveAlertsInfo &&
                <Box sx={{ ...styles.headerBadge, bgcolor: '#fff3e0', color: '#e65100' }}>
                  <span className="material-icons" style={styles.headerBadgeIcon}>
                    warning
                  </span>
                  <Typography variant="caption" fontWeight={700}>
                    {consecutive_alerts} {t('bruno.tasks_details.consecutive_alerts', 'consecutive alerts')}
                  </Typography>
                </Box>
              }
            </Box>

            {/* Close */}
            <IconButton size="small" onClick={onClose} sx={styles.closeButton} disabled={submitting}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* ── SCROLLABLE BODY ── */}
        <Box sx={styles.body}>
          <Box sx={styles.bodyContent}>

          {/* Image carousel. Skipped entirely, not rendered empty, when the
              barcode was this task type's only asset and has just become a
              button, which is the case for 58 rows across 28 chains. */}
          {(loadingImgs || showCarousel) && (
            <Box sx={styles.heroImageWrap}>
              <ImageCarousel
                images={carouselImages}
                loading={loadingImgs}
                isMobile={isMobile}
                imageHeight={isMobile ? 240 : 340}
                framing
              />
            </Box>
          )}

          {/* Details block */}
          <DetailBlock
            title={t('bruno.tasks_details.details', 'Details')}
            rows={detailRows}
          />

          {taskPrices.length > 0 && (
            <DetailDataTable
              title={t('bruno.tasks_details.prices', 'Prices')}
              columns={[
                {
                  key: 'price_type',
                  label: t('bruno.tasks_details.price_type', 'Price type'),
                  render: price => (price.price_type
                    ? t(`bruno.price_type.${price.price_type}`, price.price_type)
                    : '-'),
                },
                {
                  key: 'price',
                  label: t('bruno.tasks_details.price', 'Price'),
                  // The read price is the one that may be wrong, so it keeps the
                  // error color the rest of the app uses for it.
                  render: price => (
                    <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
                      {price.price != null ? price.price : '-'}
                    </Box>
                  ),
                },
                {
                  key: 'pos_price',
                  label: t('bruno.tasks_details.pos_price', 'POS Price'),
                  render: price => (
                    <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
                      {price.pos_price != null ? price.pos_price : '-'}
                    </Box>
                  ),
                },
              ]}
              rows={taskPrices}
              gridTemplateColumns={{ xs: '1.2fr 1fr 1fr', md: '1.4fr 1fr 1fr' }}
              getRowKey={(price, index) => price.alert_id ?? `${price.price_type}-${index}`}
            />
          )}

          {expectedLocations.length > 0 && (
            <DetailDataTable
              title={t('bruno.tasks_details.expected_locations', 'Expected locations')}
              columns={[
                { key: 'aisle', label: t('bruno.tasks_details.aisle', 'Aisle') },
                { key: 'landmark', label: t('bruno.tasks_details.landmark', 'Landmark') },
                { key: 'level', label: t('bruno.tasks_details.level', 'Level') },
                { key: 'sequence', label: t('bruno.tasks_details.sequence', 'Sequence') },
              ]}
              rows={paginatedLocations}
              gridTemplateColumns={{ xs: '1.1fr 0.8fr 0.8fr 0.8fr', md: '1.2fr 0.8fr 0.8fr 0.8fr' }}
              page={locationsPage}
              pageCount={totalLocationPages}
              onPageChange={setLocationsPage}
              getRowKey={(loc, index) => `${loc.aisle ?? 'aisle'}-${loc.landmark ?? 'landmark'}-${loc.level ?? 'level'}-${loc.sequence ?? index}`}
            />
          )}
          </Box>
        </Box>

        {/* ── ADJUSTMENT BANNER ── */}
        {adjustmentMode && (
          <Box sx={styles.adjustmentBanner}>
                  <Box component="span" className="material-icons" sx={styles.adjustmentBannerIcon}>move_to_inbox</Box>
            <Typography variant="caption" fontWeight={700} sx={styles.adjustmentBannerText}>
              {t('bruno.tasks_names.adjustment', 'Inventory adjustment')}
            </Typography>
          </Box>
        )}

        {/* ── FOOTER ── */}
        <Box sx={styles.footer}>
          {adjustmentMode ? (
            /* ── ADJUSTMENT MODE ── */
            <Box sx={styles.adjustmentMode}>
              <TextField
                  type="number"
                  value={realStock}
                  onChange={(e) => setRealStock(e.target.value)}
                  placeholder="0"
                  size="small"
                  inputProps={{ min: 0, style: { textAlign: 'center' } }}
                  sx={styles.adjustmentTextField}
                />
              <Box sx={styles.adjustmentButtons}>
                <IconButton
                  onClick={() => { setAdjustmentMode(false); setRealStock(''); }}
                  sx={styles.adjustmentCloseButton}
                >
                <Box component="span" className="material-icons" sx={styles.adjustmentButtonIcon}>close</Box>
                </IconButton>
                <IconButton
                  disabled={!isValidRealStock || submitting}
                  onClick={handleSubmitAdjustment}
                  sx={{ ...styles.adjustmentCheckButton, ...(isValidRealStock ? { boxShadow: '0 4px 12px rgba(233,30,99,0.35)' } : styles.adjustmentCheckButtonDisabled) }}
                >
                  {submitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <span className="material-icons" style={styles.adjustmentButtonIcon}>check</span>
                  )}
                </IconButton>
              </Box>
            </Box>
          ) : loadingTasksStatus ? (
            /* ── LOADING SKELETONS ── */
            <Box sx={styles.skeletonsContainer}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Box key={i} sx={styles.skeletonItem}>
                  <Skeleton variant="circular" width={44} height={44} />
                  <Skeleton variant="text" width={48} height={12} />
                </Box>
              ))}
            </Box>
          ) : !isActionable || !activeTasksStatus?.length ? (
            /* ── PENDING STATE ── */
            <Box sx={styles.pendingContainer}>
              <Box sx={styles.pendingIcon}>
                      <Box component="span" className="material-icons" sx={styles.pendingIconSpan}>hourglass_empty</Box>
              </Box>
              <Typography variant="caption" sx={styles.pendingText}>
                {t('bruno.task_status.pending_disabled', 'Pending')}
              </Typography>
            </Box>
          ) : (
            /* ── STATUS BUTTONS ── */
            <Box sx={styles.statusButtonsContainer}>
              {reversedTasksStatus.map((status) => {
                const s = getStatusStyle(status);
                const statusColor = getStatusColor(status);
                return (
                  <Box
                    key={status}
                    onClick={() => {
                      if (submitting || isCompletedTask) return;
                      if (status === 'stock_unavailable' && ask_real_stock) {
                        setAdjustmentMode(true);
                      } else {
                        handleSubmitStatus(status);
                      }
                    }}
                    sx={{
                      ...styles.statusButton,
                      ...(isCompletedTask ? styles.statusButtonDisabled : {}),
                    }}
                  >
                    <Box className="status-circle" sx={{ ...styles.statusCircle, bgcolor: `${statusColor}22` }}>
                      {submitting && selectedStatus === status ? (
                        <CircularProgress size={20} sx={{ color: statusColor }} />
                      ) : (
                        <Box component="span" className="material-icons" sx={{ ...styles.statusIcon, color: statusColor }}>
                          {s.icon}
                        </Box>
                      )}
                    </Box>
                    <Typography variant="caption" textAlign="center" sx={{ ...styles.statusLabel, color: statusColor }}>
                      {t(`bruno.task_status.${status}`, status)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
    </Box>
  );

  return (
    <BaseModal open={open} onClose={onClose}>
      {content}
    </BaseModal>
  );
}
